import React from "react";
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  ApolloLink,
  createHttpLink,
} from "@apollo/client";
import { RetryLink } from "@apollo/client/link/retry";
import { onError } from "@apollo/client/link/error";

import { useAuth } from "../Store";
import { env } from "../Constants/env";

import { headersLink, loggerResponse, useRefreshTokens } from "./apolloLink";

const GraphqlProvider: React.FC = ({ children }) => {
  const [stateAuth] = useAuth();

  const { refreshTokens } = useRefreshTokens();

  const userAccessToken = stateAuth?.userJWT?.accessToken;
  const hubAccessToken = stateAuth?.hubJWT?.accessToken;
  const legalPersonAccessToken = stateAuth?.legalPersonJWT?.accessToken;

  const accountType = stateAuth?.accountType;

  const updateJWT = {
    hubAccessToken: "",
    userAccessToken: "",
    legalPersonAccessToken: "",
  };

  const httpLink = (): ApolloLink =>
    createHttpLink({
      uri: env.graphql.uri,
      credentials: "include",
    });

  const retryIf = (error: { statusCode: number }): boolean => {
    console.log("RETRY IF", { ...error });

    const statusCode = error?.statusCode;

    // Never return statusCode
    if (statusCode && statusCode <= 500) {
      return false;
    }

    return !!error;
  };

  const retryLink = new RetryLink({
    delay: {
      initial: 100,
      max: 2000,
      jitter: true,
    },
    attempts: {
      max: 5,
      retryIf,
    },
  });

  const retryError = onError(({ graphQLErrors, operation, forward }) => {
    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        if (err?.extensions?.response?.status >= 500) {
          console.log("Retry Error", err);
          return forward(operation);
        }
      }
    }
  });

  const link = ApolloLink.from([
    loggerResponse,
    retryLink,
    retryError,
    refreshTokens(updateJWT),
    headersLink(
      updateJWT,
      accountType,
      userAccessToken,
      hubAccessToken,
      legalPersonAccessToken
    ),
    httpLink(),
  ]);

  const client = new ApolloClient({
    cache: new InMemoryCache({
      typePolicies: {
        Product: {
          keyFields: false,
        },
        BalanceEntries: {
          keyFields: false,
        },
      },
    }),
    link,
  });

  return <ApolloProvider {...{ client }}>{children}</ApolloProvider>;
};

export { GraphqlProvider };
